import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { logOut } from "./globalSlice";
import Api from "../api/api";
import { RootState } from "./store";
import { addPaginatedCollectionReducers, PaginatedCollectionState, usePaginatedCollection } from "./paginatedCollection";
import { Topup } from "../api/models";
import { addEntityFetchReducers, EntityState } from "./entity";
import { useFetchableState } from "./fetchableState";
import { redeemCodeThunk, redeemResellerCodeThunk, redemptionsThunk, redemptionThunk } from "./redemptionsSlice";
import { claimRewardThunk } from "./storeSlice";

interface TopupsState extends PaginatedCollectionState, EntityState<Topup> {
    debug: string
}

const initialState: TopupsState = {
    ids: undefined,
    link: null,
    currentRequestId: undefined,
    lastFetched: undefined,
    error: undefined,
    entities: {},
    debug: "topups state",
}

export const topupsSlice = createSlice({
    name: 'topups',
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        addEntityFetchReducers<
            Topup,
            { topup: Topup },
            { id: string, accessToken: string }
        >(
            builder,
            (payload) => payload.topup,
            topupThunk
        )
        addPaginatedCollectionReducers<
            Topup,
            { topups: Topup[], link: string | null },
            { reload: boolean, accessToken: string },
            TopupsState
        >(
            builder,
            (payload) => payload.topups,
            topupsThunk,
            topupsReducerIntercept
        )
        builder.addCase(redemptionThunk.fulfilled, (state, action) => {
            const { topups } = action.payload
            topups.forEach(topup => {
                state.entities[topup.id] = {
                    data: topup,
                    lastFetched: Date.now()
                }
            })
        })
        builder.addCase(redemptionsThunk.fulfilled, (state, action) => {
            const { topups } = action.payload
            topups.forEach(topup => {
                state.entities[topup.id] = {
                    data: topup,
                    lastFetched: Date.now()
                }
            })
        })

        // a code redemption and a reseller-code redemption side-load the same entities
        const onCodeRedeemed = (state: TopupsState, action: { payload: { topups: Topup[] } }) => {
            const { topups } = action.payload
            topups.forEach(topup => {
                state.ids = state.ids ? [topup.id, ...state.ids] : [topup.id]
                state.entities[topup.id] = {
                    data: topup,
                    lastFetched: Date.now()
                }
            })
        }
        builder.addCase(redeemCodeThunk.fulfilled, onCodeRedeemed)
        builder.addCase(redeemResellerCodeThunk.fulfilled, onCodeRedeemed)

        builder.addCase(claimRewardThunk.fulfilled, (state, action) => {
            const topups = action.payload.topups
            topups.forEach(topup => {
                state.ids = state.ids ? [topup.id, ...state.ids] : [topup.id]
                state.entities[topup.id] = {
                    data: topup,
                    lastFetched: Date.now()
                }
            })
        })
        
        builder.addCase(logOut, (state) => {
            state = initialState
        })
    },
})

export const topupThunk = createAsyncThunk('topups/fetchTopup', async ({ id, accessToken }: { id: string, accessToken: string }, { getState, requestId }) => {
    const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
    const currentRequestId = state.topups.entities[id].currentRequestId
    if (currentRequestId !== requestId)
        throw new Error("Request outdated");
    return await Api.getTopup({ id, accessToken })
})

export const topupsThunk = createAsyncThunk('topups/fetchTopups', async ({ reload, accessToken }: { reload: boolean, accessToken: string }, { getState, requestId }) => {
    const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
    const currentRequestId = state.topups.currentRequestId
    if (currentRequestId !== requestId)
        throw new Error("Request outdated");
    return await Api.listTopups({ includeExpired: true, link: state.topups.link, count: 10, accessToken })
})

const topupsReducerIntercept = (type: any, state: TopupsState, action: any) => {
    if (type === topupsThunk.fulfilled) {
        const topups: Topup[] = action.payload.topups
        topups.forEach(topup => {
            const entity = state.entities[topup.id]
            if (entity?.currentRequestId !== undefined)
                return
            state.entities[topup.id] = {
                data: topup,
                lastFetched: Date.now()
            }
        })
    }
}

export const useTopup = (id: string, accessToken: string) => {
    return useFetchableState<Topup>({
        selector: (state: RootState) => state.topups.entities[id],
        action: topupThunk({ id, accessToken })
    })
}

export const useTopups = (accessToken: string) => {
    return usePaginatedCollection({
        selector: (state: RootState) => state.topups,
        moreAction: topupsThunk({ reload: false, accessToken }),
        reloadAction: topupsThunk({ reload: true, accessToken }),
    })
}

export default topupsSlice.reducer